import { useMemo, useState, useEffect, useRef } from "react";
import { Calculator, Globe, MapPin, Package, Settings, SlidersHorizontal, User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import type { ProductTypeDefinitionDto } from "@gaole/shared";
import { SETTING_DEFS } from "@gaole/shared";
import {
  Button,
  Input,
  PageLoader,
  PillSegmentSwitcher,
  SettingsCard,
  Toggle,
} from "../../components/ui";
import { api } from "../../lib/api";
import { ShippingRatesEditor } from "./ShippingRatesEditor";
import { CountryRatesEditor } from "./CountryRatesEditor";
import { ShippingAdvancedEditor } from "./ShippingAdvancedEditor";
import { ShippingSimulator } from "./ShippingSimulator";
import { PickupLocationsEditor } from "./PickupLocationsEditor";

type ShippingSubTab = "countries" | "states" | "general" | "sender" | "advanced" | "simulator" | "pickup";

const SHIPPING_KEYS = SETTING_DEFS.filter((d) => d.group === "shipping").map((d) => d.key);

const senderDefs = SETTING_DEFS.filter(
  (d) => d.group === "shipping" && d.key.startsWith("shipping.sender."),
);
const generalDefs = SETTING_DEFS.filter(
  (d) =>
    d.group === "shipping" &&
    !d.key.startsWith("shipping.sender.") &&
    d.key !== "shipping.state_rates" &&
    d.key !== "shipping.country_rates" &&
    d.key !== "shipping.advanced" &&
    !d.key.startsWith("shipping.pickup."),
);

const pickupToggleDef = SETTING_DEFS.find((d) => d.key === "shipping.pickup.enabled")!;
const pickupTimingDefs = SETTING_DEFS.filter((d) =>
  ["shipping.pickup.min_lead_hours", "shipping.pickup.max_days_ahead"].includes(d.key),
);

function SettingField({
  def,
  value,
  onChange,
}: {
  def: (typeof SETTING_DEFS)[number];
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const hint = def.description;
  if (def.type === "number") {
    return (
      <Input
        label={def.label}
        hint={hint}
        type="number"
        step="0.01"
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <Input
      label={def.label}
      hint={hint}
      value={String(value ?? "")}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function ShippingSettingsEditor({
  draft,
  setValue,
  saveMut,
}: {
  draft: Record<string, unknown>;
  setValue: (key: string, value: unknown) => void;
  saveMut: Pick<UseMutationResult<unknown, Error, string[]>, "mutate" | "isPending">;
}) {
  const [subTab, setSubTab] = useState<ShippingSubTab>("countries");
  const [savedSnapshot, setSavedSnapshot] = useState<Record<string, unknown>>({});
  const snapshotHydrated = useRef(false);

  useEffect(() => {
    if (snapshotHydrated.current || Object.keys(draft).length === 0) return;
    setSavedSnapshot({ ...draft });
    snapshotHydrated.current = true;
  }, [draft]);

  const productTypesQuery = useQuery({
    queryKey: ["catalog", "product-types", "shipping"],
    queryFn: () => api<{ types: ProductTypeDefinitionDto[] }>("/catalog/product-types"),
  });

  const productsQuery = useQuery({
    queryKey: ["products", "shipping-simulator"],
    queryFn: () =>
      api<{ items: { id: string; name: string; sku: string; type: string; weightGrams: number }[] }>(
        "/catalog/products?pageSize=100&forInventory=true&behavior=SINGLE",
      ),
  });

  const productTypeOptions = useMemo(
    () =>
      (productTypesQuery.data?.types ?? [])
        .filter((t) => t.active)
        .map((t) => ({ value: t.key, label: t.label })),
    [productTypesQuery.data?.types],
  );

  const productOptions = useMemo(
    () =>
      (productsQuery.data?.items ?? []).map((p) => ({
        value: p.id,
        label: p.name,
        hint: p.sku,
        weightGrams: p.weightGrams,
        productType: p.type,
      })),
    [productsQuery.data?.items],
  );

  const cancel = () => {
    for (const key of SHIPPING_KEYS) {
      setValue(key, savedSnapshot[key] ?? draft[key]);
    }
  };

  const save = () => {
    saveMut.mutate(SHIPPING_KEYS, {
      onSuccess: () => {
        setSavedSnapshot((prev) => {
          const next = { ...prev };
          for (const key of SHIPPING_KEYS) next[key] = draft[key];
          return next;
        });
      },
    });
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="ghost" size="sm" disabled={saveMut.isPending} onClick={cancel}>
        Cancel
      </Button>
      <Button size="sm" disabled={saveMut.isPending} onClick={save}>
        Save
      </Button>
    </div>
  );

  const fallbackFee = Number(draft["shipping.flat_fee"] ?? 10);

  const subTabs = useMemo(
    () => [
      { value: "countries" as const, label: "Countries", icon: Globe },
      { value: "states" as const, label: "States", icon: MapPin },
      { value: "general" as const, label: "General", icon: Settings },
      { value: "sender" as const, label: "Sender", icon: User },
      { value: "pickup" as const, label: "Pickup", icon: Package },
      { value: "advanced" as const, label: "Advanced", icon: SlidersHorizontal },
      { value: "simulator" as const, label: "Simulator", icon: Calculator },
    ],
    [],
  );

  if (productTypesQuery.isLoading && (subTab === "advanced" || subTab === "simulator")) {
    return <PageLoader />;
  }

  return (
    <div className="w-full min-w-0 space-y-5">
      <div>
        <h1 className="font-display text-lg font-bold">Shipping</h1>
        <p className="mt-0.5 text-xs text-muted">
          Country and state delivery fees, advanced surcharges, sender details, and fee simulation.
        </p>
      </div>

      <PillSegmentSwitcher
        value={subTab}
        onChange={setSubTab}
        options={subTabs.map(({ value, label }) => ({ value, label }))}
      />

      {subTab === "countries" && (
        <SettingsCard
          title="International country rates"
          description="Flat delivery fee per country outside Malaysia."
          footer={footer}
        >
          <CountryRatesEditor
            value={draft["shipping.country_rates"]}
            fallbackFee={fallbackFee}
            onChange={(rates) => setValue("shipping.country_rates", rates)}
          />
        </SettingsCard>
      )}

      {subTab === "states" && (
        <SettingsCard
          title="Malaysia state rates"
          description="One flat fee per Malaysian state. Unknown states use the default fee for other countries."
          footer={footer}
        >
          <ShippingRatesEditor
            value={draft["shipping.state_rates"]}
            fallbackFee={fallbackFee}
            onChange={(rates) => setValue("shipping.state_rates", rates)}
          />
        </SettingsCard>
      )}

      {subTab === "general" && (
        <SettingsCard title="General" footer={footer}>
          <div className="space-y-4">
            {generalDefs.map((def) => (
              <SettingField
                key={def.key}
                def={def}
                value={draft[def.key] ?? def.defaultValue}
                onChange={(v) => setValue(def.key, v)}
              />
            ))}
          </div>
        </SettingsCard>
      )}

      {subTab === "sender" && (
        <SettingsCard
          title="Sender details"
          description="Used for fulfilment exports and carrier bookings."
          footer={footer}
        >
          <div className="space-y-4">
            {senderDefs.map((def) => (
              <SettingField
                key={def.key}
                def={def}
                value={draft[def.key] ?? def.defaultValue}
                onChange={(v) => setValue(def.key, v)}
              />
            ))}
          </div>
        </SettingsCard>
      )}

      {subTab === "pickup" && (
        <SettingsCard
          title="Store pickup"
          description="Let customers collect orders at your store. Pickup is free; staff mark orders delivered when collected."
          footer={footer}
        >
          <div className="space-y-4">
            <Toggle
              label={pickupToggleDef.label}
              checked={Boolean(draft["shipping.pickup.enabled"] ?? pickupToggleDef.defaultValue)}
              onChange={(v) => setValue("shipping.pickup.enabled", v)}
            />
            {pickupToggleDef.description && (
              <p className="text-xs text-muted">{pickupToggleDef.description}</p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {pickupTimingDefs.map((def) => (
                <SettingField
                  key={def.key}
                  def={def}
                  value={draft[def.key] ?? def.defaultValue}
                  onChange={(v) => setValue(def.key, v)}
                />
              ))}
            </div>
            <PickupLocationsEditor
              value={draft["shipping.pickup.locations"]}
              onChange={(locations) => setValue("shipping.pickup.locations", locations)}
            />
          </div>
        </SettingsCard>
      )}

      {subTab === "advanced" && (
        <ShippingAdvancedEditor
          value={draft["shipping.advanced"]}
          productTypeOptions={productTypeOptions}
          onChange={(next) => setValue("shipping.advanced", next)}
          footer={footer}
        />
      )}

      {subTab === "simulator" && (
        <ShippingSimulator
          draft={draft}
          productTypeOptions={productTypeOptions}
          productOptions={productOptions}
        />
      )}
    </div>
  );
}
